import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  SelectGroup,
  SelectListSingle,
  Table,
  TableRow,
  TableCell,
  TableColumnHeader,
  CardTitle,
  InfoTooltip,
  Tag,
  ListboxGroup,
  GoodsFamily,
  aggregatedGoodCategoriesByGoodFamilies
} from '@/common'
import useTranslatedZodErrors from '@/shared/hooks/useTranslatedZodErrors'
import { StepFooter } from '@/shared/components'
import { definitionSchema, RouteProductionMethod } from '@/features/production-routes/api/ProductionRouteModel'
import GoodSelectorDialog from '../dialogs/GoodSelectorDialog'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import { INTEGRATED_PRODUCTION_METHODS_COMBINATIONS } from '@/shared/config/config'
import { TrashIcon } from '@/common/components/icons'
import { usePermissions } from '@/shared/hooks/usePermissions'

export default function ProductionRouteDefinition({
  next,
  goodsFamily,
  initialState
}: {
  next: (payload: { aggregatedGood: string; productionMethods: RouteProductionMethod[] }) => void
  goodsFamily?: GoodsFamily
  initialState: {
    aggregatedGood: string
    productionMethods: RouteProductionMethod[]
  }
}) {
  const initializeCnCodes = () => {
    const cnCodes: { productionMethodCode: string; cnCode: string }[] = []
    if (initialState.productionMethods) {
      initialState.productionMethods.forEach(method => {
        method.cnCodes.forEach(cnCode => {
          cnCodes.push({ productionMethodCode: method.code, cnCode })
        })
      })
    }
    return cnCodes
  }
  const { t } = useTranslation()
  const { productionMethods } = useAppConfig().config
  const [isCnCodesDialogOpen, setIsCnCodesDialogOpen] = useState(false)
  const { checkPermission } = usePermissions()
  const hasInternalPermission = checkPermission('internal', 'create')
  const [aggregatedGoodCategory, setAggregatedGoodCategory] = useState<string | null>(
    initialState.aggregatedGood
  )

  const [productionMethodCodes, setProductionMethodCodes] = useState<string[] | null>(
    initialState.productionMethods
      ? initialState.productionMethods.map(method => method.code)
      : null
  )

  const [cnCodes, setCnCodes] = useState<
    | {
        productionMethodCode: string
        cnCode: string
      }[]
    | null
  >(initializeCnCodes())

  const handleRemoveCnCode = useCallback((cnCode: string) => {
    setCnCodes(prev => prev?.filter(value => value.cnCode !== cnCode) || null)
  }, [])

  const [selectedIntegratedProductionMethods, setSelectedIntegratedProductionMethods] = useState<
    string[]
  >(productionMethodCodes && productionMethodCodes.length > 1 ? productionMethodCodes : [])
  const [productionMethodCodeForGoodSelection, setProductionMethodCodeForGoodSelection] = useState<
    string | null
  >(null)

  const [isProductionRouteApplicable, setIsProductionRouteApplicable] = useState(
    selectedIntegratedProductionMethods.length > 1
  )

  const [isProductionRouteApplicableSelectDisabled, setIsProductionRouteApplicableSelectDisabled] =
    useState(false)

  const productionMethodsWithCnCodes = useMemo(() => {
    return productionMethodCodes?.map(productionMethodCode => {
      const addedProductionMethodCnCodes = cnCodes
        ?.filter(cnCode => cnCode.productionMethodCode === productionMethodCode)
        .map(cnCode => cnCode.cnCode)

      return {
        code: productionMethodCode,
        cnCodes: addedProductionMethodCnCodes
      }
    })
  }, [cnCodes, productionMethodCodes])

  const productionMethodsForCategory = useMemo(
    () =>
      productionMethods.filter(method => method.aggregatedGoodsCategory === aggregatedGoodCategory),
    [aggregatedGoodCategory, productionMethods]
  )

  const lastProductionMethod = useMemo(() => {
    return productionMethodCodes && productionMethodCodes.length > 0
      ? productionMethodCodes[productionMethodCodes.length - 1]
      : null
  }, [productionMethodCodes])

  const integratedProductionMethodsOptions = useMemo(() => {
    if (
      !lastProductionMethod ||
      !INTEGRATED_PRODUCTION_METHODS_COMBINATIONS[lastProductionMethod]
    ) {
      setIsProductionRouteApplicable(false)
      setIsProductionRouteApplicableSelectDisabled(true)
      return []
    }
    setIsProductionRouteApplicableSelectDisabled(false)
    return INTEGRATED_PRODUCTION_METHODS_COMBINATIONS[lastProductionMethod].map(combination => ({
      label: combination.codes.map(code => t(`common:productionMethods.${code}`)).join(' → '),
      value: combination.codes.join(',')
    }))
  }, [lastProductionMethod, t])

  const onAggregatedGoodsChange = useCallback(
    (value: string) => {
      setAggregatedGoodCategory(value)
      const methods = productionMethods.filter(method => method.aggregatedGoodsCategory === value)

      if (methods.length === 1) {
        setProductionMethodCodes([methods[0].code])
      } else {
        setProductionMethodCodes(null)
      }
      setSelectedIntegratedProductionMethods([])
      setCnCodes(null)
    },
    [productionMethods]
  )

  const onProductionMethodChange = useCallback((value: string) => {
    setProductionMethodCodes([value])
  }, [])

  const onIntegratedProductionMethodChange = useCallback(
    (value: string) => {
      const productionMethodsArray = value.split(',').map(method => method.trim())
      if (value !== '') {
        setSelectedIntegratedProductionMethods(productionMethodsArray)
        setProductionMethodCodes(productionMethodsArray)
        setCnCodes(null)
      } else {
        setSelectedIntegratedProductionMethods([])
        setProductionMethodCodes(lastProductionMethod ? [lastProductionMethod] : null)
        setCnCodes(null)
      }
    },
    [lastProductionMethod]
  )

  const onProductionMethodApplicableChange = useCallback(
    (isApplicable: boolean) => {
      setCnCodes(null)
      setIsProductionRouteApplicable(isApplicable)
      if (!isApplicable) {
        setProductionMethodCodes(lastProductionMethod ? [lastProductionMethod] : null)
      }
    },
    [lastProductionMethod]
  )

  const onAddGoodsCode = (value: string) => {
    const hasBeenAlreadyAdded = cnCodes?.some(cnCode => cnCode.cnCode === value)
    if (!hasBeenAlreadyAdded) {
      setCnCodes(prev =>
        prev
          ? [
              ...prev,
              {
                productionMethodCode: productionMethodCodeForGoodSelection!,
                cnCode: value
              }
            ]
          : [{ productionMethodCode: productionMethodCodeForGoodSelection!, cnCode: value }]
      )
    }
  }

  const aggregatedGoodsOptions = goodsFamily
    ? aggregatedGoodCategoriesByGoodFamilies[goodsFamily].map(aggregatedGood => ({
        label: t(`common:aggregatedGoods.${aggregatedGood.value}`),
        value: aggregatedGood.value,
        disabled: !aggregatedGood.isReportingEnabled && !hasInternalPermission,
        tooltip:
          !aggregatedGood.isReportingEnabled && !hasInternalPermission
            ? t('common:outsidePlanTooltip')
            : undefined
      }))
    : []

  const validationResult = definitionSchema.safeParse({
    aggregatedGood: aggregatedGoodCategory,
    productionMethods: productionMethodsWithCnCodes
  })

  const { showToast } = useTranslatedZodErrors({
    stringKeysPrefix: 'inputFields'
  })

  const productionMethodOptions = useMemo(() => {
    return (
      productionMethodsForCategory.map(method => ({
        label: t(`common:productionMethods.${method.code}`),
        value: method.code
      })) || []
    )
  }, [productionMethodsForCategory, t])

  const isNextButtonEnabled =
    !!aggregatedGoodCategory && !!productionMethodCodes && cnCodes !== null

  const onNextClick = useCallback(() => {
    if (!validationResult.success) {
      showToast(validationResult.error.issues)
      return
    }
    next(validationResult.data)
  }, [validationResult, next, showToast])

  return (
    <>
      <GoodSelectorDialog
        productionMethodCode={productionMethodCodeForGoodSelection}
        isOpen={isCnCodesDialogOpen}
        onClose={() => setIsCnCodesDialogOpen(false)}
        add={onAddGoodsCode}
      />
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle title={t('productionRoute:goodsAndProductionMethods.general.title')} />
          </CardHeader>
          <CardBody className="space-y-4 w-2/4">
            <ListboxGroup
              label={t('productionRoute:goodsAndProductionMethods.general.aggregatedGoods.label')}
              value={aggregatedGoodCategory || ''}
              options={aggregatedGoodsOptions}
              onChange={onAggregatedGoodsChange}
            />
            <ListboxGroup
              label={t('productionRoute:goodsAndProductionMethods.general.productionMethod.label')}
              disabled={productionMethodOptions.length <= 1}
              value={lastProductionMethod || ''}
              options={productionMethodOptions}
              onChange={onProductionMethodChange}
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle
              title={t(
                'productionRoute:goodsAndProductionMethods.integratedProductionRoutes.title'
              )}
            />
          </CardHeader>
          <CardBody>
            <SelectListSingle
              className="space-y-2"
              name="integratedProductionRoutes"
              onChange={onProductionMethodApplicableChange}
              disabled={isProductionRouteApplicableSelectDisabled}
              items={[
                {
                  id: 'notApplicable',
                  label: t(
                    'productionRoute:goodsAndProductionMethods.integratedProductionRoutes.options.notApplicable'
                  ),
                  value: false,
                  isChecked: !isProductionRouteApplicable
                },
                {
                  id: 'applicable',
                  label: t(
                    'productionRoute:goodsAndProductionMethods.integratedProductionRoutes.options.applicable'
                  ),
                  value: true,
                  isChecked: isProductionRouteApplicable,
                  isOpen: isProductionRouteApplicable,
                  children: (
                    <div className="w-2/3">
                      <SelectGroup
                        label={t(
                          'productionRoute:goodsAndProductionMethods.integratedProductionRoutes.integratedProductionMethod.label'
                        )}
                        value={selectedIntegratedProductionMethods.join(',')}
                        options={integratedProductionMethodsOptions}
                        onChange={evt => onIntegratedProductionMethodChange(evt.target.value)}
                      />
                    </div>
                  )
                }
              ]}
            />
          </CardBody>
        </Card>
        <div className="transition duration-300 ease-in data-[closed]:opacity-0">
          <Card>
            <CardHeader className="flex items-center">
              <CardTitle title={t('productionRoute:goodsAndProductionMethods.goodsCodes.title')} />
              <InfoTooltip
                content={t('productionRoute:goodsAndProductionMethods.goodsCodes.tooltip')}
              />
            </CardHeader>
            <div className="space-y-4">
              {lastProductionMethod ? (
                <Table dense borderless>
                  <thead>
                    <TableRow>
                      <TableColumnHeader className="w-1/3">
                        {t(
                          'productionRoute:goodsAndProductionMethods.goodsCodes.table.productionMethod'
                        )}
                      </TableColumnHeader>
                      <TableColumnHeader className="w-2/3">
                        {t('productionRoute:goodsAndProductionMethods.goodsCodes.table.goodsCode')}
                      </TableColumnHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {productionMethodCodes?.map(method => (
                      <TableRow key={method}>
                        <TableCell>
                          <h4 className="font-semibold text-sm">
                            {t(`common:productionMethods.${method}`)}
                          </h4>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-4 items-center">
                            {cnCodes
                              ?.filter(cnCode => cnCode.productionMethodCode === method)
                              ?.map(cnCode => {
                                return (
                                  <div key={cnCode.productionMethodCode}>
                                    <Tag
                                      label={
                                        <div className="flex space-x-2 items-center">
                                          <span className="font-mono">{cnCode.cnCode}</span>
                                          <button
                                            onClick={() => {
                                              handleRemoveCnCode(cnCode.cnCode)
                                            }}
                                          >
                                            <TrashIcon className="size-5" />
                                          </button>
                                        </div>
                                      }
                                      title={t(`goods:${cnCode.cnCode}`)}
                                    />
                                  </div>
                                )
                              })}
                            <Button
                              disabled={!productionMethodCodes}
                              sizeType="sm"
                              variant="txt-default"
                              onClick={() => {
                                setProductionMethodCodeForGoodSelection(method)
                                setIsCnCodesDialogOpen(true)
                              }}
                            >
                              {t(
                                'productionRoute:goodsAndProductionMethods.goodsCodes.addGoodsCodeButton'
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <CardBody>
                  <p className="text-center text-sm text-gray-500">
                    {t('productionRoute:goodsAndProductionMethods.goodsCodes.noProductionMethod')}
                  </p>
                </CardBody>
              )}
            </div>
          </Card>
        </div>
        <StepFooter onNext={onNextClick} isNextEnabled={isNextButtonEnabled} />
      </div>
    </>
  )
}
